<script setup>
// SidebarNodeActions — botão ⋯ + dropdown menu por contexto.
// Renderizado pelo SidebarTreeNode quando o node define `actions`.
// Cada action: { label, icon?, to?, action?, value?, isDestructive? }
//   - to: navega via router (router-link)
//   - action: emite via DropdownMenu (consumido por handlers externos)
//
// Atalho: 1 única action com `to` e `inline: true` (ou sem dropdown
// necessário) vira botão direto sem ⋯ + popover — visual mais limpo
// pro padrão "+ Adicionar X" em headers.
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  actions: { type: Array, required: true },
});

const emit = defineEmits(['action']);

const router = useRouter();
const isOpen = ref(false);
const containerRef = ref(null);

// Modo inline: 1 única action vira link/botão direto, sem dropdown.
// Mantém o padrão visual do "+ Novo" em headers (clean — sem ⋯ + popover).
const isInline = computed(() => props.actions.length === 1);
const inlineAction = computed(() => (isInline.value ? props.actions[0] : null));
const isInlineLink = computed(() => isInline.value && !!inlineAction.value?.to);
const isInlineButton = computed(
  () => isInline.value && !inlineAction.value?.to
);

const handleInlineButton = e => {
  e.preventDefault();
  e.stopPropagation();
  emit('action', {
    action: inlineAction.value.action,
    value: inlineAction.value.value,
  });
};

onClickOutside(containerRef, () => {
  isOpen.value = false;
});

const handleToggle = e => {
  // Impede que o click chegue no <a> wrapper do TreeNode (que navegaria).
  e.preventDefault();
  e.stopPropagation();
  isOpen.value = !isOpen.value;
};

const handleAction = payload => {
  isOpen.value = false;
  // Items com `to` navegam direto; resto emite pra fora.
  const original = props.actions.find(a => a.value === payload.value);
  if (original?.to) router.push(original.to);
  else emit('action', payload);
};

// DropdownMenu valida que cada item tenha action/value/label.
// Mapeia pra esse contrato com defaults razoáveis.
const dropdownItems = () =>
  props.actions.map((a, i) => ({
    label: a.label,
    icon: a.icon,
    action: a.action || 'open',
    value: a.value ?? i,
    ...(a.isDestructive ? { action: 'delete' } : {}),
  }));
</script>

<template>
  <!-- Inline link: 1 ação com `to` → router-link direto -->
  <router-link
    v-if="isInlineLink"
    v-tooltip.right="inlineAction.label"
    :to="inlineAction.to"
    :aria-label="inlineAction.label"
    class="grid place-content-center size-5 rounded text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
    @click.stop
  >
    <Icon :icon="inlineAction.icon || 'i-lucide-plus'" class="size-3.5" />
  </router-link>

  <!-- Inline button: 1 ação sem `to` (só emit) → botão direto sem popover -->
  <button
    v-else-if="isInlineButton"
    v-tooltip.right="inlineAction.label"
    type="button"
    :aria-label="inlineAction.label"
    class="grid place-content-center size-5 rounded text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
    @click="handleInlineButton"
  >
    <Icon :icon="inlineAction.icon || 'i-lucide-plus'" class="size-3.5" />
  </button>

  <!-- Modo padrão: botão ⋯ + dropdown -->
  <span v-else ref="containerRef" class="relative inline-flex">
    <button
      v-tooltip.right="$t('SIDEBAR.NODE_ACTIONS')"
      type="button"
      class="grid place-content-center size-5 rounded text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
      :aria-label="$t('SIDEBAR.NODE_ACTIONS')"
      :aria-expanded="isOpen"
      @click="handleToggle"
    >
      <Icon icon="i-lucide-more-horizontal" class="size-3.5" />
    </button>
    <DropdownMenu
      v-if="isOpen"
      :menu-items="dropdownItems()"
      class="mt-1 ltr:right-0 rtl:left-0 top-full"
      @action="handleAction"
    />
  </span>
</template>
